import { memo, type CSSProperties, type ReactNode } from 'react';
import classes from './DataRow.module.css';

interface ChipSpec {
  text: string;
  /** Hex colour from upstream profession/trade data; rendered as inline style.
   * This is the single accepted exception to the "no raw colors" rule —
   * profession colours are upstream per-trade data, not design-token choices.
   * Validated against a `#RRGGBB`/`#RGB`/`#RRGGBBAA` shape before being passed
   * to React's `style` prop (defense-in-depth against any future upstream
   * shape change that would otherwise allow arbitrary CSS injection). */
  backgroundColor?: string;
  tradeId?: string;
}

interface IDataRowProps {
  label: string;
  chip?: ChipSpec;
  /** Slot rendered to the left of the label (e.g. tag glyph, severity dot). */
  leadingIcon?: ReactNode;
  /** Renders a warning glyph between the label and the chip. */
  warning?: boolean;
}

const HEX_COLOR_RE = /^#[0-9a-fA-F]{3,8}$/;

/**
 * Shared list row for analytics report sections. The parent renders the
 * `<ul>`; this is the `<li>`. Used by DailyRoute's {Activities, Equipment,
 * Personnel}Section and ObserveLocation's {Activities, SafetyEvents}Panel.
 *
 * `chip.backgroundColor` is rendered inline because the value comes from
 * upstream profession data — see the comment on ChipSpec.
 *
 * Wrapped in `memo` — list contexts can render 20+ rows per section across
 * three sections; the props (label/chip/leadingIcon/warning) are stable
 * across most parent re-renders so shallow comparison short-circuits.
 */
function DataRowImpl({ label, chip, leadingIcon, warning }: IDataRowProps) {
  const safeBackground =
    chip?.backgroundColor && HEX_COLOR_RE.test(chip.backgroundColor)
      ? chip.backgroundColor
      : undefined;
  const chipStyle: CSSProperties | undefined = safeBackground
    ? { backgroundColor: safeBackground }
    : undefined;
  const chipClass = safeBackground
    ? classes.chip
    : `${classes.chip} ${classes.chipNeutral}`;
  return (
    <li className={classes.row}>
      <span className={classes.left}>
        {leadingIcon}
        <span className={classes.label}>{label}</span>
      </span>
      {warning && (
        <svg
          className={classes.warning}
          data-testid="data-row-warning"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            d="M6.13 14.63L10.51 5.88c.62-1.23 2.37-1.23 2.98 0l4.38 8.75c.56 1.11-.25 2.41-1.49 2.41H7.62c-1.24 0-2.05-1.31-1.49-2.41Z"
            fill="currentColor"
          />
        </svg>
      )}
      {chip && (
        <span className={chipClass} style={chipStyle}>
          {chip.text}
        </span>
      )}
    </li>
  );
}

export const DataRow = memo(DataRowImpl);
